<template>
  <g>
    <g v-if="config.tool !== 'eraser'" ref="svg" />
    <circle
      r="1e10"
      fill="transparent"
      stroke="transparent"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseLeave"
      @mouseenter="onMouseEnter"
      @mousewheel="onMouseWheel"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    />
    <g v-if="config.tool === 'eraser'" ref="svg" />
  </g>
</template>

<script>
import * as d3 from 'd3'
import px from 'vue-types'

import { deepFreeze } from '@/helper.js'
import deepClone from '@/utils/deepClone.js'

import drawing from './drawing.js'

const newDrawing = config => ({
  v: 'v1',
  config: {
    color: config.color,
    opacity: config.opacity,
    width: config.width,
    tool: config.tool,
  },
  path: [],
})

export default {
  name: 'MapDrawing',
  props: {
    config: px.shape({
      color: px.oneOf([0, 1, 2]).isRequired,
      mode: px.oneOf(['on', 'ctrl', 'alt']),
      opacity: px.oneOf([0, 1, 2]).isRequired,
      width: px.oneOf([0, 1, 2]).isRequired,
      tool: px.oneOf(['pencil', 'arrow', 'circle', 'line', 'text', 'arrow-vector', 'circle-vector', 'line-vector'])
        .isRequired,
    }).isRequired,
    strokes: px.arrayOf(
      px.oneOfType([
        px.shape({
          v: px.oneOf(['v1']).isRequired,
          config: px.shape({
            color: px.oneOf([0, 1, 2]).isRequired,
            opacity: px.oneOf([0, 1, 2]).isRequired,
            width: px.oneOf([0, 1, 2]).isRequired,
          }).isRequired,
          path: px.arrayOf(
            px.shape({
              x: px.number.isRequired,
              y: px.number.isRequired,
            })
          ).isRequired,
        }),
      ])
    ).isRequired,
    enabled: px.bool.def(true),
  },
  data() {
    return {
      currentDrawing: newDrawing(this.config),
    }
  },
  watch: {
    config(config) {
      this.currentDrawing.config.color = config.color
      this.currentDrawing.config.opacity = config.opacity
      this.currentDrawing.config.width = config.width
      this.currentDrawing.config.tool = config.tool
    },
    enabled(value) {
      if (!value) {
        this.finishDraw()
      }
    },
    strokes() {
      this.update()
    },
  },
  mounted() {
    window.addEventListener('wheel', this.onMapMouseWheel)
    this.pt = this.$el.ownerSVGElement.createSVGPoint()
    this.update()
  },
  beforeDestroy() {
    window.removeEventListener('wheel', this.onMapMouseWheel)
    this.pt = null
  },
  methods: {
    cursorPoint(evt) {
      this.pt.x = evt.clientX
      this.pt.y = evt.clientY
      const localPt = this.pt.matrixTransform(this.$el.getScreenCTM().inverse())
      return Object.freeze({ x: localPt.x, y: localPt.y })
    },
    isDrawing(e) {
      if (!this.enabled) return false

      switch (this.config.mode) {
        case 'on':
          return e.buttons & 1
        case 'alt':
          return e.altKey && e.buttons & 1
        case 'ctrl':
          return e.ctrlKey && e.buttons & 1
        default:
          throw new Error(`unhandled drawing mode ${this.config.mode}`)
      }
    },
    startDraw() {},
    resetDraw() {
      this.currentDrawing = newDrawing(this.config)

      this.update()
    },
    finishDraw() {
      if (this.currentDrawing.path.length > 1) {
        if (this.currentDrawing.config.tool === 'text') {
          this.$emit('typeText', deepClone(this.currentDrawing))
        } else {
          this.$emit('update', deepFreeze(deepClone(this.currentDrawing)))
        }
      }

      this.resetDraw()
    },
    onTouchStart(e) {
      if (!this.enabled) return false
      if (e.targetTouches.length === 1) {
        e.preventDefault()
        this.startDraw()
      }
    },
    onTouchMove(e) {
      if (!this.enabled) return false
      if (e.targetTouches.length === 1) {
        e.preventDefault()
        this.currentDrawing.path.push(this.cursorPoint(e.targetTouches[0]))
        this.update()
      } else {
        this.resetDraw()
      }
    },
    onTouchEnd() {
      if (!this.enabled) return false
      this.finishDraw()
    },
    onMouseDown(e) {
      if (this.isDrawing(e)) {
        e.preventDefault()
        this.startDraw()
      } else {
        if (this.$listeners.mousedown) {
          this.$listeners.mousedown(e, this.cursorPoint(e))
        }
      }
    },
    onMouseEnter(e) {
      if (this.isDrawing(e)) {
        this.startDraw()
      } else {
        if (this.$listeners.mouseenter) {
          this.$listeners.mouseenter(e, this.cursorPoint(e))
        }
      }
    },
    onMouseMove(e) {
      if (this.isDrawing(e)) {
        e.preventDefault()
        this.currentDrawing.path.push(this.cursorPoint(e))
        this.update()
      } else {
        if (this.$listeners.mousemove) {
          this.$listeners.mousemove(e, this.cursorPoint(e))
        }
      }
    },
    onMouseUp(e) {
      this.finishDraw()
      if (this.$listeners.mouseup) {
        this.$listeners.mouseup(e, this.cursorPoint(e))
      }
    },
    onMouseLeave(e) {
      this.finishDraw()
      if (this.$listeners.mouseleave) {
        this.$listeners.mouseleave(e, this.cursorPoint(e))
      }
    },
    onMouseWheel(e) {
      if (this.$listeners.mousewheel) {
        this.$listeners.mousewheel(e, this.cursorPoint(e))
      }
    },
    update() {
      if (!this.$refs.svg) {
        this.$nextTick(this.update)
        return
      }

      const allDrawings = [...this.strokes, this.currentDrawing]
        .filter(d => d.path.length > 1)
        .map((d, i) => ({ ...d, index: i }))
      const pathDrawings = allDrawings.filter(d => d.config.tool === 'pencil')
      const circleDrawings = allDrawings.filter(d => d.config.tool === 'circle')
      const lineDrawings = allDrawings.filter(d => d.config.tool === 'line')
      const rectDrawings = allDrawings.filter(d => d.config.tool === 'text')
      const arrowDrawings = allDrawings.filter(d => d.config.tool === 'arrow')

      const svg = d3.select(this.$refs.svg)

      svg
        .selectAll('path.pencil')
        .data(pathDrawings, (e, i) => i)
        .join('path')
        .attr('d', d => d.path.map((e, i) => `${i > 0 ? 'L' : 'M'}${e.x},${e.y}`).join('\n'))
        .attr('stroke', d => drawing.colors[d.config.color])
        .attr('stroke-width', d => drawing.widths[d.config.width])
        .attr('fill', 'none')
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'pencil')
        .on('mouseenter', e => {
          d3.select(e.target).transition().duration(100).attr('opacity', 0.7).attr('class', 'pencil eraser')
        })
        .on('mouseleave', (e, d) => {
          d3.select(e.target)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'pencil')
        })
        .on('click', (e, d) => {
          if (this.config.tool === 'eraser') {
            this.$emit('erase', d.index)
          }
        })

      d3.select(this.$refs.svg)
        .selectAll('line.line')
        .data(lineDrawings, (e, i) => i)
        .join('line')
        .attr('x1', d => d.path[0].x)
        .attr('y1', d => d.path[0].y)
        .attr('x2', d => d.path[d.path.length - 1].x)
        .attr('y2', d => d.path[d.path.length - 1].y)
        .attr('stroke', d => drawing.colors[d.config.color])
        .attr('stroke-width', d => drawing.widths[d.config.width])
        .attr('fill', 'none')
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'line eraser')
        .on('mouseenter', e => {
          d3.select(e.target).transition().duration(100).attr('opacity', 0.7).attr('class', 'line eraser')
        })
        .on('mouseleave', (e, d) => {
          d3.select(e.target)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'line')
        })
        .on('click', (e, d) => {
          if (this.config.tool === 'eraser') {
            this.$emit('erase', d.index)
          }
        })

      d3.select(this.$refs.svg)
        .selectAll('foreignObject.text')
        .data(
          rectDrawings.filter(d => d.text),
          (e, i) => i
        )
        .join('foreignObject')
        .attr('x', d => Math.min(d.path[0].x, d.path[d.path.length - 1].x))
        .attr('y', d => Math.min(d.path[0].y, d.path[d.path.length - 1].y))
        .attr('width', d => Math.abs(d.path[d.path.length - 1].x - d.path[0].x))
        .attr('height', d => Math.abs(d.path[d.path.length - 1].y - d.path[0].y))
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'text')
        .html(
          d =>
            `<div class="drawText ${this.calcTextSize(d)}" style="color: ${drawing.colors[d.config.color]};">${
              d.text
            }</div>`
        )
        .on('mouseenter', (e, d) => {
          d3.select(e.target).transition().duration(100).attr('opacity', 0.7).attr('class', 'text eraser')
          d3.select(`#text-rect-${d.index}`)
            .transition()
            .duration(100)
            .attr('opacity', 0.7)
            .attr('class', 'text eraser')
        })
        .on('mouseleave', (e, d) => {
          d3.select(e.target)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'text')

          d3.select(`#text-rect-${d.index}`)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'text')
        })
        .on('click', (e, d) => {
          if (this.config.tool === 'eraser') {
            this.$emit('erase', d.index)
          }
        })

      d3.select(this.$refs.svg)
        .selectAll('rect.text')
        .data([...rectDrawings], (e, i) => i)
        .join('rect')
        .attr('x', d => Math.min(d.path[0].x, d.path[d.path.length - 1].x))
        .attr('y', d => Math.min(d.path[0].y, d.path[d.path.length - 1].y))
        .attr('width', d => Math.abs(d.path[d.path.length - 1].x - d.path[0].x))
        .attr('height', d => Math.abs(d.path[d.path.length - 1].y - d.path[0].y))
        .attr('stroke', d => drawing.colors[d.config.color])
        .attr('stroke-width', 2)
        .attr('fill', 'none')
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'text')
        .attr('id', d => `text-rect-${d.index}`)

      d3.select(this.$refs.svg)
        .selectAll('g.arrow')
        .data(arrowDrawings, (e, i) => i)
        .join('g')
        .attr('fill', d => drawing.colors[d.config.color])
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'arrow')
        .attr('transform', d => {
          const flipX = d.path[0].x > d.path[d.path.length - 1].x
          const flipY = d.path[0].y > d.path[d.path.length - 1].y
          const x = Math.min(d.path[0].x, d.path[d.path.length - 1].x)
          const y = Math.min(d.path[0].y, d.path[d.path.length - 1].y)
          const width = Math.abs(d.path[d.path.length - 1].x - d.path[0].x)
          const height = Math.abs(d.path[d.path.length - 1].y - d.path[0].y)
          return `translate(${x + width / 2}, ${y}) scale(${(width / 344.51) * (flipX ? 1 : -1)}, ${
            (height / 386.78) * (flipY ? -1 : 1)
          }) translate(${-344.51 / 2}, ${flipY ? -386.78 : 0})`
        })
        .on('mouseenter', e => {
          d3.select(e.target).transition().duration(100).attr('opacity', 0.7).attr('class', 'arrow eraser')
        })
        .on('mouseleave', (e, d) => {
          d3.select(e.target)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'arrow')
        })
        .on('click', (e, d) => {
          if (this.config.tool === 'eraser') {
            this.$emit('erase', d.index)
          }
        })
        .selectAll('path')
        .data([
          'M131.31,244a255.86,255.86,0,0,0-111,94.1q1.8-58.65.5-117.2c-.3-12.1-19-12.1-18.8,0q1.65,78.3-2,156.5c-.4,8.9,13.7,13.4,17.5,4.8a239.85,239.85,0,0,1,123.2-121.9c11.2-4.9,1.6-21.1-9.4-16.3Z',
          'M333.11.57c-95.6,33.6-176.4,98.9-238,178.5a676.23,676.23,0,0,0-47.9,70.8c-6.1,10.4,10.1,19.9,16.2,9.5,49-83.1,116.1-157.2,200-206a444,444,0,0,1,74.7-34.6c11.3-4,6.4-22.2-5-18.2ZM333.11.57c-95.6,33.6-176.4,98.9-238,178.5a676.23,676.23,0,0,0-47.9,70.8c-6.1,10.4,10.1,19.9,16.2,9.5,49-83.1,116.1-157.2,200-206a444,444,0,0,1,74.7-34.6c11.3-4,6.4-22.2-5-18.2Z',
        ])
        .join('path')
        .attr('d', d => d)

      d3.select(this.$refs.svg)
        .selectAll('g.circle')
        .data(circleDrawings, (e, i) => i)
        .join('g')
        .attr('fill', d => drawing.colors[d.config.color])
        .attr('opacity', d => drawing.opacities[d.config.opacity])
        .attr('class', 'circle')
        .attr('viewBox', '0 0 385.82 397.16')
        .attr('x', d => Math.min(d.path[0].x, d.path[d.path.length - 1].x))
        .attr('y', d => Math.min(d.path[0].y, d.path[d.path.length - 1].y))
        .attr('width', d => Math.abs(d.path[d.path.length - 1].x - d.path[0].x))
        .attr('height', d => Math.abs(d.path[d.path.length - 1].y - d.path[0].y))
        .attr('transform', d => {
          const flipX = d.path[0].x > d.path[d.path.length - 1].x
          const flipY = d.path[0].y > d.path[d.path.length - 1].y
          const x = Math.min(d.path[0].x, d.path[d.path.length - 1].x)
          const y = Math.min(d.path[0].y, d.path[d.path.length - 1].y)
          const width = Math.abs(d.path[d.path.length - 1].x - d.path[0].x)
          const height = Math.abs(d.path[d.path.length - 1].y - d.path[0].y)
          return `translate(${x + width / 2}, ${y}) scale(${(width / 344.51) * (flipX ? 1 : -1)}, ${
            (height / 386.78) * (flipY ? -1 : 1)
          }) translate(${-344.51 / 2}, ${flipY ? -386.78 : 0})`
        })
        .on('mouseenter', e => {
          d3.select(e.target).transition().duration(100).attr('opacity', 0.7).attr('class', 'circle eraser')
        })
        .on('mouseleave', (e, d) => {
          d3.select(e.target)
            .transition()
            .duration(100)
            .attr('opacity', drawing.opacities[d.config.opacity])
            .attr('class', 'circle')
        })
        .on('click', (e, d) => {
          if (this.config.tool === 'eraser') {
            this.$emit('erase', d.index)
          }
        })
        .selectAll('path')
        .data([
          'M147.4,397c-3.71-.13-7.4-.47-11.09-.78-1.66-.13-3.3-.41-4.94-.61-1.47-.19-2.92-.39-4.38-.63-3.77-.62-7.5-1.31-11.25-2.06-3.56-.71-7.1-1.56-10.64-2.39-1.51-.35-3-.81-4.51-1.21s-2.74-.72-4.09-1.14c-3.63-1.12-7.23-2.23-10.8-3.51l-5-1.76c-1.9-.68-3.75-1.58-5.6-2.36-3-1.24-5.78-2.73-8.64-4.15-1.82-.89-3.53-2-5.27-3-1.46-.85-2.88-1.74-4.27-2.7l-4.56-3.13c-.56-.39-1.09-.88-1.62-1.31l-3-2.45c-1.34-1.07-2.57-2.29-3.82-3.46s-2.39-2.26-3.53-3.48c-2.5-2.66-4.86-5.4-7.2-8.2C31,346,29,343.15,27.05,340.32c-1.85-2.65-3.65-5.37-5.29-8.16-.87-1.48-1.81-2.94-2.61-4.46l-2.85-5.39c-1.64-3.1-3.05-6.35-4.41-9.58s-2.69-6.52-3.79-9.87S6,296.26,5,292.91s-1.75-6.55-2.34-9.86A149.2,149.2,0,0,1,.23,263c-.28-7.05-.34-14.07,0-21.12s1-14.06,2-21,2.42-13.88,4-20.75c1.44-6.41,3.17-12.82,5.18-19.07,2.17-6.76,4.48-13.44,7.13-20s5.5-12.84,8.55-19.13a261.53,261.53,0,0,1,20.06-34.22c3.49-5.08,7.07-10.16,11-14.9,2-2.39,4-4.74,6.15-6.94a13.4,13.4,0,0,1,3.6-3,.39.39,0,0,1,.35,0h0a0,0,0,0,1,0,0s-.06,0-.09,0h-.07l-.18,0h0l-.38.19h0l-.63.46h0a45.34,45.34,0,0,0-3.28,3.37h0c-2.5,3-4.79,6.07-7,9.24h0l0,.05,0,0c-3,4.42-5.82,8.9-8.53,13.48s-5.11,9.2-7.5,13.88l.09-.17c-2.39,4.89-4.78,9.81-6.85,14.86-2.17,5.32-4.25,10.66-6.18,16.09-1.94,5.77-3.71,11.57-5.4,17.41s-3,12-4.32,18.05c-1.36,6.92-2.63,13.84-3.63,20.82S12.59,224.79,12,232q-.29,6.2-.4,12.4c-.07,4.11.16,8.23.35,12.34.25,3,.5,5.93.87,8.89s.89,6.08,1.42,9.12,1.32,6,2.13,8.92,1.76,5.92,2.72,8.87c1.12,2.94,2.22,5.86,3.46,8.75s2.47,5.49,3.79,8.21,2.74,5.15,4.22,7.67,3.21,5.2,4.87,7.78c1.58,2.25,3.15,4.48,4.8,6.66,1.79,2.34,3.65,4.6,5.53,6.86,1.2,1.31,2.4,2.61,3.59,3.92s2.82,2.76,4.22,4.13L57.67,350c.83.71,1.78,1.32,2.65,2l2.35,1.75L67.94,357c1,.65,2.17,1.17,3.25,1.75l2.67,1.43,6.57,3c1.11.5,2.3.87,3.43,1.3l3.43,1.3c2,.62,4,1.27,6,1.9,1.69.53,3.41,1,5.13,1.42,3.7,1,7.42,1.85,11.15,2.67,4.85.93,9.7,1.75,14.58,2.52s9.64,1.15,14.47,1.62c3.33.18,6.65.36,10,.41,3.59.06,7.17,0,10.76-.14,3.31-.24,6.62-.49,9.93-.87,3.71-.44,7.38-1,11.06-1.65s7.06-1.51,10.56-2.41c3.79-1,7.61-2,11.32-3.26l6.6-2.27c3.88-1.5,7.76-3,11.61-4.6s7.62-3.33,11.4-5.07l7.25-3.61c4.85-2.48,9.53-5.29,14.23-8s9.17-5.74,13.66-8.74l6.63-4.68c2.27-1.6,4.44-3.35,6.66-5s4.34-3.49,6.5-5.24c2-1.57,3.83-3.26,5.72-4.91q3-2.72,5.93-5.41c2-1.87,4-3.85,6-5.78s3.81-3.89,5.72-5.82,3.86-4.07,5.71-6.16l2.54-2.88-.45.51c.3-.35.61-.7.92-1l-.06.06,1-1.13-.37.41.47-.52.23-.27L320,288c3.33-3.9,6.5-7.92,9.66-12,2.94-3.76,5.62-7.72,8.33-11.65,2.54-3.93,5-7.87,7.45-11.87s4.49-8,6.62-12.08c.9-1.84,1.79-3.67,2.68-5.51,1-2,1.85-4.18,2.77-6.26,1.72-3.89,3.08-7.93,4.49-11.94,1-3.11,1.92-6.21,2.74-9.35s1.54-6.35,2.24-9.55,1.1-6.3,1.53-9.47.73-6.37,1-9.56c.11-1.84.21-3.68.32-5.52.13-2.11.09-4.24.14-6.36.08-4-.17-8-.37-12-.37-4.57-.77-9.13-1.42-13.67s-1.49-9.33-2.42-14c-.63-2.7-1.25-5.39-1.95-8.07s-1.5-5.29-2.31-7.93c-1.14-3.26-2.34-6.49-3.63-9.7S355,95.14,353.45,92c-1.7-3.15-3.42-6.26-5.2-9.36-1.68-2.92-3.56-5.73-5.41-8.54s-3.81-5.37-5.77-8-4.05-5-6.14-7.51c-1.6-1.75-3.18-3.49-4.84-5.18s-3.51-3.43-5.31-5.11c-1.74-1.49-3.48-3-5.29-4.36-2.05-1.59-4.14-3.09-6.26-4.58-2.08-1.33-4.16-2.66-6.3-3.9-2.32-1.35-4.67-2.6-7-3.84-3.14-1.5-6.28-2.93-9.47-4.31s-6.2-2.4-9.33-3.51-6.47-2-9.73-3-6.6-1.67-9.92-2.43S250.51,17,247,16.5c-3.91-.57-7.82-1-11.77-1.34-2.78-.15-5.56-.26-8.36-.32s-5.68.1-8.51.23c-4,.3-7.9.71-11.84,1.22s-7.76,1.27-11.61,2a247.66,247.66,0,0,0-26,6.87c-2.79,1-5.54,2.06-8.28,3.19s-5.39,2.44-8.05,3.74a303.71,303.71,0,0,0-30.89,18A220.41,220.41,0,0,0,102.2,65l.83-.73-1.16,1a9,9,0,0,1-6.1,2.53,8.64,8.64,0,0,1-8.64-8.64,8.42,8.42,0,0,1,2.53-6.1,214.3,214.3,0,0,1,32.32-24q8.59-5.16,17.53-9.64a155.12,155.12,0,0,1,17.93-8,206.33,206.33,0,0,1,39.72-9.36A175.88,175.88,0,0,1,238.86.64,189.33,189.33,0,0,1,259.48,3.9a187.12,187.12,0,0,1,19.46,5.17,161.62,161.62,0,0,1,19.25,7.47,140.07,140.07,0,0,1,19.6,10.82c2.91,2,5.75,4,8.57,6.16s5.43,4.61,8.05,7a135.32,135.32,0,0,1,14.13,15.74A171.23,171.23,0,0,1,360,72.92a171.58,171.58,0,0,1,9.76,17.8c1.53,3.3,3,6.61,4.4,10s2.43,6.68,3.52,10.06c1,3,1.8,6,2.55,9.09.83,3.4,1.64,6.82,2.25,10.27.66,3.71,1.25,7.41,1.78,11.14.47,3.29.72,6.63,1,9.94.57,7.14.66,14.26.52,21.41a195.16,195.16,0,0,1-1.86,20.91c-.42,3.29-1.08,6.57-1.74,9.82-.75,3.62-1.58,7.2-2.46,10.8a169.44,169.44,0,0,1-6.44,19.64,186.72,186.72,0,0,1-8.59,18.76,248.67,248.67,0,0,1-18.29,30c-3.57,5.06-7.29,10-11.14,14.82S327.17,307,323,311.66l.77-.88c-4.55,5.17-9.34,10.15-14.28,14.94-2.75,2.66-5.54,5.24-8.38,7.8s-5.77,5-8.71,7.4c-2.32,1.93-4.74,3.79-7.18,5.57-2.79,2-5.56,4.08-8.41,6.05-6.21,4.29-12.49,8.44-18.91,12.41a238.14,238.14,0,0,1-37.15,18.49c-3.46,1.39-6.94,2.65-10.46,3.9-3.37,1.21-6.8,2.23-10.22,3.29-1.58.48-3.21.83-4.8,1.25-1.41.37-2.82.74-4.24,1.05-3.91.82-7.8,1.63-11.74,2.26-3.54.57-7.08.95-10.65,1.33-1.78.19-3.58.24-5.37.34-1.54.1-3.08.21-4.62.23l-6.17.07h-.37C150.56,397.16,149,397.05,147.4,397Z',
        ])
        .join('path')
        .attr('d', d => d)
    },
    calcMidpoint(p1, p2) {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      }
    },
    calcDistance(p1, p2) {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
    },
    calcTextSize(drawing) {
      switch (drawing.config.width) {
        case 1:
          return 'medium'
        case 2:
          return 'large'
        default:
          return 'small'
      }
    },
  },
}
</script>

<style>
.eraser {
  cursor: pointer;
}
</style>
